import { useEffect, useMemo, useState } from 'react';
import { richieste } from '../../api/acquisti';
import type { DettaglioRichiesta, MaterialeDaPreventivo } from '../../api/acquisti';
import { formattaNumero } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';

/*  IL «TASTINO» CHE PESCA DAL PREVENTIVO. 16/10/2026, Galizzi: «poi un tastino che
    clicco li' e mi va a pescare tutto l'elenco di quello che ho preventivato. Lo
    selezioni, poi puoi aggiungere anche altre cose».

    L'elenco sono gli articoli che il cliente ha preso, raggruppati per capitolo, con
    accanto quanto se n'e' gia' chiesto. Si parte con niente spuntato: e' la richiesta
    di oggi, non tutto il cantiere. La quantita' proposta e' quella che resta da
    chiedere, e si corregge — oggi servono dieci metri dei venti preventivati.

    Quelli gia' chiesti per intero non si vedono, se non lo si chiede: sono la maggior
    parte della lista a cantiere avviato, e in mezzo a loro le cose che mancano si
    perdono.  */
export function DalPreventivo({
  richiesta,
  onAggiunte,
  onChiudi,
}: {
  richiesta: DettaglioRichiesta;
  onAggiunte: (aggiornata: DettaglioRichiesta) => void;
  onChiudi: () => void;
}) {
  const [materiali, setMateriali] = useState<MaterialeDaPreventivo[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [spuntati, setSpuntati] = useState<Record<string, string>>({});
  const [ancheChiesti, setAncheChiesti] = useState(false);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    richieste
      .materialiPreventivo(richiesta.commessaId)
      .then(setMateriali)
      .catch((e: Error) => setErrore(e.message));
  }, [richiesta.commessaId]);

  // Le righe di preventivo gia' in questa richiesta non si possono rimettere.
  const giaQui = useMemo(
    () => new Set(richiesta.righe.map((r) => r.rigaPreventivoId).filter((x): x is string => x !== null)),
    [richiesta.righe],
  );

  const visibili = (materiali ?? []).filter(
    (m) => !giaQui.has(m.rigaPreventivoId) && (ancheChiesti || m.daRichiedere > 0),
  );
  const nascosti = (materiali ?? []).filter((m) => !giaQui.has(m.rigaPreventivoId)).length - visibili.length;

  const scelte = Object.entries(spuntati)
    .map(([id, q]) => ({ rigaPreventivoId: id, quantita: numeroDecimale(q) ?? 0 }));
  const valide = scelte.length > 0 && scelte.every((s) => s.quantita > 0);

  function spunta(m: MaterialeDaPreventivo, acceso: boolean) {
    setSpuntati((prima) => {
      const dopo = { ...prima };
      if (acceso) dopo[m.rigaPreventivoId] = String(m.daRichiedere > 0 ? m.daRichiedere : m.preventivata);
      else delete dopo[m.rigaPreventivoId];
      return dopo;
    });
  }

  async function metti() {
    setInCorso(true);
    setErrore(null);
    try {
      onAggiunte(await richieste.aggiungiDalPreventivo(richiesta.id, scelte));
      onChiudi();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  let capitoloPrima: string | null | undefined;

  return (
    <section className="dal-preventivo" aria-label="Materiali dal preventivo">
      <header>
        <h3>Dal preventivo</h3>
        <label className="anche-chiesti">
          <input type="checkbox" checked={ancheChiesti} onChange={(e) => setAncheChiesti(e.target.checked)} />
          mostra anche quelli già chiesti{nascosti > 0 && !ancheChiesti && ` (${nascosti})`}
        </label>
      </header>

      {errore && <p className="errore">{errore}</p>}
      {materiali === null && !errore && <p className="attesa">Leggo il preventivo…</p>}
      {materiali !== null && materiali.length === 0 && (
        <p className="nota">Questa commessa non ha materiali preventivati da chiedere.</p>
      )}
      {materiali !== null && materiali.length > 0 && visibili.length === 0 && (
        <p className="nota">Del preventivo è già stato chiesto tutto.</p>
      )}

      {visibili.length > 0 && (
        <table className="confronto">
          <thead>
            <tr>
              <th />
              <th>Cosa</th>
              <th className="destra">Preventivati</th>
              <th className="destra">Già chiesti</th>
              <th className="destra">Da chiedere ora</th>
            </tr>
          </thead>
          <tbody>
            {visibili.map((m) => {
              const nuovoCapitolo = m.capitolo !== capitoloPrima;
              capitoloPrima = m.capitolo;
              const acceso = m.rigaPreventivoId in spuntati;

              return [
                nuovoCapitolo && m.capitolo !== null && (
                  <tr key={`c-${m.rigaPreventivoId}`} className="capitolo">
                    <td colSpan={5}>{m.capitolo}</td>
                  </tr>
                ),
                <tr key={m.rigaPreventivoId} className={acceso ? 'selezionata' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Chiedi ${m.descrizione}`}
                      checked={acceso}
                      onChange={(e) => spunta(m, e.target.checked)}
                    />
                  </td>
                  <td>
                    <span className="descrizione">{m.descrizione}</span>
                    {m.siglaProduttore && (
                      <em className="chiave">
                        {' '}
                        {m.siglaProduttore} {m.codiceArticoloProduttore}
                      </em>
                    )}
                  </td>
                  <td className="destra">
                    {formattaNumero(m.preventivata)} {m.unitaMisura}
                  </td>
                  <td className="destra">{m.giaRichiesta > 0 ? formattaNumero(m.giaRichiesta) : '—'}</td>
                  <td className="destra">
                    {acceso ? (
                      <input
                        className="quantita"
                        inputMode="decimal"
                        aria-label={`Quantità di ${m.descrizione}`}
                        value={spuntati[m.rigaPreventivoId]}
                        onChange={(e) =>
                          setSpuntati((prima) => ({ ...prima, [m.rigaPreventivoId]: e.target.value }))
                        }
                      />
                    ) : (
                      formattaNumero(m.daRichiedere)
                    )}
                  </td>
                </tr>,
              ];
            })}
          </tbody>
        </table>
      )}

      <p className="azioni">
        <button type="button" className="primario" disabled={inCorso || !valide} onClick={() => void metti()}>
          {scelte.length <= 1 ? 'Metti nella richiesta' : `Metti le ${scelte.length} righe nella richiesta`}
        </button>
        <button type="button" disabled={inCorso} onClick={onChiudi}>
          Chiudi
        </button>
      </p>
    </section>
  );
}
